import { useDataTable } from '@hooks/table/useDataTable';
import { type ColumnDef, flexRender, type Row } from '@tanstack/react-table';
import React from 'react';

/**
 * Small helper to combine classNames conditionally
 */
const cn = (...classes: Array<string | false | null | undefined>) =>
    classes.filter(Boolean).join(' ');

/**
 * All the override-able classNames for styling control.
 */
export interface DataTableClasses {
    container?: string;

    // toolbar
    toolbar?: string;
    toolbarLeft?: string;
    toolbarRight?: string;
    searchInput?: string;

    // table wrapper
    tableWrapper?: string;
    table?: string;
    thead?: string;
    headerRow?: string;
    headerCell?: string;
    tbody?: string;
    bodyRow?: string;
    bodyCell?: string;

    // footer / pagination
    footer?: string;
    paginationInfo?: string;
    paginationButton?: string;
    pageSizeSelect?: string;
}

/**
 * Public props for the DataTable component.
 */
export interface DataTableProps<TData extends object> {
    data: TData[];
    columns: ColumnDef<TData, any>[];

    // search + filter
    enableGlobalSearch?: boolean;
    searchPlaceholder?: string;

    // pagination
    enablePagination?: boolean;
    pageSizeOptions?: number[];
    initialPageSize?: number;

    // extra UI (toolbar)
    toolbarLeftContent?: React.ReactNode;
    toolbarRightContent?: React.ReactNode;
    emptyState?: React.ReactNode;

    // styling control
    classes?: DataTableClasses;

    // per-row class (e.g. highlight rows)
    getRowClassName?: (row: Row<TData>) => string | undefined;
}

/**
 * Generic DataTable
 */
export function DataTable<TData extends object>({
    data,
    columns,
    enableGlobalSearch = true,
    searchPlaceholder = 'Search…',
    enablePagination = true,
    pageSizeOptions = [10, 20, 50],
    initialPageSize,
    toolbarLeftContent,
    toolbarRightContent,
    emptyState,
    classes,
    getRowClassName,
}: DataTableProps<TData>) {
    // hook call
    const {
        table,
        globalFilter,
        setGlobalFilter,
        pageSizeOptions: internalPageSizeOptions,
    } = useDataTable<TData>({
        data,
        columns,
        enableGlobalSearch,
        enablePagination,
        pageSizeOptions,
        initialPageSize,
    });

    const currentPageSize = table.getState().pagination.pageSize;
    const hasRows = table.getRowModel().rows.length > 0;

    return (
        <div
            className={cn(
                'sm:px- w-full border-slate-200 bg-slate-50 px-0 py-2 sm:py-2',
                classes?.container,
            )}
        >
            {/* ───────────────── Toolbar (search + left/right actions) ───────────────── */}
            <div
                className={cn(
                    'mb-3 flex flex-col gap-2 sm:mb-4 sm:flex-row sm:items-center sm:justify-between',
                    classes?.toolbar,
                )}
            >
                {/* Left side: global search + extra stuff */}
                <div
                    className={cn(
                        'flex flex-1 items-center gap-2',
                        classes?.toolbarLeft,
                    )}
                >
                    {enableGlobalSearch && (
                        <input
                            value={globalFilter ?? ''}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            placeholder={searchPlaceholder}
                            className={cn(
                                'w-full max-w-xs rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-800 shadow-sm focus:border-slate-400 focus:ring-0 focus:outline-none sm:text-sm',
                                classes?.searchInput,
                            )}
                        />
                    )}

                    {toolbarLeftContent}
                </div>

                {/* Right side: buttons / filters / etc. */}
                <div
                    className={cn(
                        'mt-1 flex items-center gap-2 sm:mt-0',
                        classes?.toolbarRight,
                    )}
                >
                    {toolbarRightContent}
                </div>
            </div>

            {/* ───────────────── Table wrapper (scroll + borders) ────────────────────── */}
            <div className={cn('rounded-xl sm:p-1', classes?.tableWrapper)}>
                <div className="w-full overflow-x-auto">
                    <table
                        className={cn(
                            'w-full min-w-full border-collapse text-[11px] text-slate-800 sm:text-[13px]',
                            classes?.table,
                        )}
                    >
                        {/* ───────── HEAD (supports multi-row / grouped headers) ─────── */}
                        <thead
                            className={cn(
                                'bg-slate-50 text-[10px] text-slate-500 sm:text-xs',
                                classes?.thead,
                            )}
                        >
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    key={headerGroup.id}
                                    className={cn(
                                        'border-b border-slate-200',
                                        classes?.headerRow,
                                    )}
                                >
                                    {headerGroup.headers.map((header) => {
                                        const canSort =
                                            header.column.getCanSort();
                                        const sortDir =
                                            header.column.getIsSorted(); // false | 'asc' | 'desc'

                                        return (
                                            <th
                                                key={header.id}
                                                colSpan={header.colSpan}
                                                className={cn(
                                                    'px-3 py-2 text-left font-medium select-none sm:px-4',
                                                    canSort
                                                        ? 'cursor-pointer hover:bg-slate-100/70'
                                                        : '',
                                                    classes?.headerCell,
                                                )}
                                                onClick={
                                                    canSort
                                                        ? header.column.getToggleSortingHandler()
                                                        : undefined
                                                }
                                            >
                                                <div className="flex items-center justify-center gap-1">
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column
                                                                  .columnDef
                                                                  .header,
                                                              header.getContext(),
                                                          )}

                                                    {canSort && (
                                                        <span className="text-[10px] text-slate-400">
                                                            {sortDir === 'asc'
                                                                ? '▲'
                                                                : sortDir ===
                                                                    'desc'
                                                                  ? '▼'
                                                                  : '⇅'}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>

                        {/* ───────── BODY ───────── */}
                        <tbody className={classes?.tbody}>
                            {hasRows ? (
                                table.getRowModel().rows.map((row) => (
                                    <tr
                                        key={row.id}
                                        className={cn(
                                            'border-b border-slate-100 last:border-b-0 hover:bg-slate-50/80',
                                            classes?.bodyRow,
                                            getRowClassName?.(row),
                                        )}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <td
                                                key={cell.id}
                                                className={cn(
                                                    'px-3 py-2 align-middle sm:px-4',
                                                    classes?.bodyCell,
                                                )}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan={
                                            table.getAllLeafColumns().length
                                        }
                                        className="px-3 py-6 text-center text-xs text-slate-400 sm:px-4"
                                    >
                                        {emptyState ?? 'No records found.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ───────────────── Footer / Pagination ──────────────────────── */}
            {enablePagination && (
                <div
                    className={cn(
                        'mt-3 flex flex-col gap-2 text-[11px] sm:mt-4 sm:flex-row sm:items-center sm:justify-between sm:text-xs',
                        classes?.footer,
                    )}
                >
                    {/* Left: page info */}
                    <div
                        className={cn(
                            'flex items-center gap-2',
                            classes?.paginationInfo,
                        )}
                    >
                        <span>
                            Page{' '}
                            <strong>
                                {table.getState().pagination.pageIndex + 1} of{' '}
                                {table.getPageCount() || 1}
                            </strong>
                        </span>
                        <span className="hidden sm:inline">
                            • {data.length} total rows
                        </span>
                    </div>

                    {/* Right: page size + controls */}
                    <div className="flex items-center gap-2">
                        <select
                            value={currentPageSize}
                            onChange={(e) =>
                                table.setPageSize(Number(e.target.value))
                            }
                            className={cn(
                                'rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] shadow-sm focus:outline-none',
                                classes?.pageSizeSelect,
                            )}
                        >
                            {internalPageSizeOptions.map((size) => (
                                <option key={size} value={size}>
                                    Show {size}
                                </option>
                            ))}
                        </select>

                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => table.firstPage()}
                                disabled={!table.getCanPreviousPage()}
                                className={cn(
                                    'rounded-full border border-slate-200 bg-white px-2 py-1 disabled:cursor-not-allowed disabled:opacity-40',
                                    classes?.paginationButton,
                                )}
                            >
                                «
                            </button>
                            <button
                                type="button"
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                                className={cn(
                                    'rounded-full border border-slate-200 bg-white px-2 py-1 disabled:cursor-not-allowed disabled:opacity-40',
                                    classes?.paginationButton,
                                )}
                            >
                                ‹
                            </button>
                            <button
                                type="button"
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                                className={cn(
                                    'rounded-full border border-slate-200 bg-white px-2 py-1 disabled:cursor-not-allowed disabled:opacity-40',
                                    classes?.paginationButton,
                                )}
                            >
                                ›
                            </button>
                            <button
                                type="button"
                                onClick={() => table.lastPage()}
                                disabled={!table.getCanNextPage()}
                                className={cn(
                                    'rounded-full border border-slate-200 bg-white px-2 py-1 disabled:cursor-not-allowed disabled:opacity-40',
                                    classes?.paginationButton,
                                )}
                            >
                                »
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
